import { useState, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BusinessIcon from '@mui/icons-material/Business';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { alpha, useTheme } from '@mui/material/styles';
import type { TreeRow } from '../hooks/useBudgetImport';

/* ─── Types ─── */

interface KontoNode {
  konto: string;
  kontoNavn: string | null;
  budsjett: number;
  antallAvtaler: number;
  dekning: number;
}

interface AnsvarNode {
  ansvar: string;
  ansvarNavn: string | null;
  kontoer: KontoNode[];
  budsjett: number;
  dekning: number;
  antallAvtaler: number;
}

interface RammeNode {
  rammeomraadeID: string;
  rammeomraadeNavn: string | null;
  ansvarListe: AnsvarNode[];
  budsjett: number;
  dekning: number;
  antallAvtaler: number;
}

interface SektorNode {
  sektorID: string;
  sektorNavn: string | null;
  rammeomraader: RammeNode[];
  budsjett: number;
  dekning: number;
  antallAvtaler: number;
}

/* ─── Helpers ─── */

function formatKr(value: number | null): string {
  if (value == null || value === 0) return '\u2014';
  return value.toLocaleString('nb-NO', { maximumFractionDigits: 0 }) + ' kr';
}

function dekningsgrad(dekning: number, budsjett: number): number | null {
  if (!budsjett || budsjett <= 0) return null;
  return Math.min(dekning / budsjett, 1);
}

function dekColor(pct: number | null): 'success' | 'warning' | 'error' | 'inherit' {
  if (pct == null) return 'inherit';
  if (pct >= 0.8) return 'success';
  if (pct >= 0.5) return 'warning';
  return 'error';
}

const SEKTOR_COLORS: Record<string, string> = {
  POL: '#1565C0',
  OPP: '#2E7D32',
  HO:  '#C62828',
  MV:  '#6A1B9A',
  TEK: '#E65100',
};

/* ─── Build tree ─── */

function buildTree(rows: TreeRow[]): { tree: SektorNode[]; totalBudsjett: number; totalDekning: number } {
  const sektorMap = new Map<string, SektorNode>();

  for (const row of rows) {
    const sId = row.SektorID || '_ukjent';
    const rId = row.RammeomraadeID || '_ukjent';

    if (!sektorMap.has(sId)) {
      sektorMap.set(sId, {
        sektorID: sId,
        sektorNavn: row.SektorNavn || 'Ukjent sektor',
        rammeomraader: [],
        budsjett: 0,
        dekning: 0,
        antallAvtaler: 0,
      });
    }
    const sektor = sektorMap.get(sId)!;

    let ramme = sektor.rammeomraader.find((r) => r.rammeomraadeID === rId);
    if (!ramme) {
      ramme = {
        rammeomraadeID: rId,
        rammeomraadeNavn: row.RammeomraadeNavn || 'Ukjent rammeområde',
        ansvarListe: [],
        budsjett: 0,
        dekning: 0,
        antallAvtaler: 0,
      };
      sektor.rammeomraader.push(ramme);
    }

    let ansvar = ramme.ansvarListe.find((a) => a.ansvar === row.Ansvar);
    if (!ansvar) {
      ansvar = {
        ansvar: row.Ansvar,
        ansvarNavn: row.AnsvarNavn,
        kontoer: [],
        budsjett: 0,
        dekning: 0,
        antallAvtaler: 0,
      };
      ramme.ansvarListe.push(ansvar);
    }

    const budsjett = row.Budsjett ?? 0;
    const dekning = row.Dekning ?? 0;
    const avtaler = row.AntallAvtaler ?? 0;

    ansvar.kontoer.push({
      konto: row.Konto,
      kontoNavn: row.KontoNavn,
      budsjett,
      antallAvtaler: avtaler,
      dekning,
    });

    ansvar.budsjett += budsjett;
    ansvar.dekning += dekning;
    ansvar.antallAvtaler += avtaler;
    ramme.budsjett += budsjett;
    ramme.dekning += dekning;
    ramme.antallAvtaler += avtaler;
    sektor.budsjett += budsjett;
    sektor.dekning += dekning;
    sektor.antallAvtaler += avtaler;
  }

  const tree = Array.from(sektorMap.values()).sort((a, b) =>
    (a.sektorNavn || '').localeCompare(b.sektorNavn || '', 'nb')
  );

  const totalBudsjett = tree.reduce((s, n) => s + n.budsjett, 0);
  const totalDekning = tree.reduce((s, n) => s + n.dekning, 0);

  return { tree, totalBudsjett, totalDekning };
}

/* ─── Sub-components ─── */

function DekBar({ budsjett, dekning }: { budsjett: number; dekning: number }) {
  const pct = dekningsgrad(dekning, budsjett);
  if (pct == null) return null;
  const color = dekColor(pct);

  return (
    <Tooltip title={`Dekningsgrad: ${(pct * 100).toFixed(0)} %`} arrow>
      <Box sx={{ width: 100, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <LinearProgress
          variant="determinate"
          value={pct * 100}
          color={color === 'inherit' ? 'primary' : color}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            bgcolor: (t) => alpha(t.palette.grey[300], 0.4),
          }}
        />
        <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right', fontWeight: 600, fontSize: '0.7rem' }}>
          {(pct * 100).toFixed(0)}%
        </Typography>
      </Box>
    </Tooltip>
  );
}

function ExpandIcon({ open }: { open: boolean }) {
  return open ? (
    <ExpandMoreIcon sx={{ fontSize: 20, transition: 'transform 0.2s' }} />
  ) : (
    <ChevronRightIcon sx={{ fontSize: 20, transition: 'transform 0.2s' }} />
  );
}

/* ─── Row components ─── */

function KontoRow({ node }: { node: KontoNode }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: 11,
        pr: 2,
        py: 0.5,
        '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) },
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
      }}
    >
      <AccountBalanceWalletIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 1 }} />
      <Typography variant="body2" sx={{ width: 70, fontFamily: 'monospace', color: 'text.secondary' }}>
        {node.konto}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }} noWrap>
        {node.kontoNavn || node.konto}
      </Typography>
      <Typography
        variant="body2"
        sx={{ width: 130, textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}
      >
        {formatKr(node.budsjett)}
      </Typography>
      <Box sx={{ width: 140, display: 'flex', justifyContent: 'center' }}>
        <DekBar budsjett={node.budsjett} dekning={node.dekning} />
      </Box>
      <Box sx={{ width: 70, textAlign: 'center' }}>
        {node.antallAvtaler > 0 ? (
          <Chip label={node.antallAvtaler} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        ) : (
          <Typography variant="caption" color="text.disabled">\u2014</Typography>
        )}
      </Box>
    </Box>
  );
}

function AnsvarRow({ node, expanded, onToggle }: { node: AnsvarNode; expanded: boolean; onToggle: () => void }) {
  const theme = useTheme();
  return (
    <>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 7,
          pr: 2,
          py: 0.6,
          cursor: 'pointer',
          '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.06) },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        }}
      >
        <ExpandIcon open={expanded} />
        <GroupWorkIcon sx={{ fontSize: 18, color: 'text.secondary', mx: 0.5 }} />
        <Typography variant="body2" sx={{ width: 60, fontFamily: 'monospace', fontWeight: 600 }}>
          {node.ansvar}
        </Typography>
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>
          {node.ansvarNavn || node.ansvar}
        </Typography>
        <Typography
          variant="body2"
          sx={{ width: 130, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}
        >
          {formatKr(node.budsjett)}
        </Typography>
        <Box sx={{ width: 140, display: 'flex', justifyContent: 'center' }}>
          <DekBar budsjett={node.budsjett} dekning={node.dekning} />
        </Box>
        <Box sx={{ width: 70, textAlign: 'center' }}>
          <Chip
            label={node.kontoer.length}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.1) }}
          />
        </Box>
      </Box>
      <Collapse in={expanded} timeout={200}>
        {node.kontoer.map((k) => (
          <KontoRow key={k.konto} node={k} />
        ))}
      </Collapse>
    </>
  );
}

function RammeRow({
  node,
  expanded,
  onToggle,
  openSet,
  toggleNode,
}: {
  node: RammeNode;
  expanded: boolean;
  onToggle: () => void;
  openSet: Set<string>;
  toggleNode: (id: string) => void;
}) {
  const theme = useTheme();
  return (
    <>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 3.5,
          pr: 2,
          py: 0.7,
          cursor: 'pointer',
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          '&:hover': { bgcolor: alpha(theme.palette.grey[200], 0.6) },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <ExpandIcon open={expanded} />
        <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary', mx: 0.5 }} />
        <Typography variant="body2" sx={{ width: 50, fontFamily: 'monospace', fontWeight: 700 }}>
          {node.rammeomraadeID}
        </Typography>
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap>
          {node.rammeomraadeNavn}
        </Typography>
        <Typography
          variant="body2"
          sx={{ width: 130, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}
        >
          {formatKr(node.budsjett)}
        </Typography>
        <Box sx={{ width: 140, display: 'flex', justifyContent: 'center' }}>
          <DekBar budsjett={node.budsjett} dekning={node.dekning} />
        </Box>
        <Box sx={{ width: 70, textAlign: 'center' }}>
          <Chip
            label={`${node.antallAvtaler}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>
      </Box>
      <Collapse in={expanded} timeout={200}>
        {node.ansvarListe.map((a) => (
          <AnsvarRow
            key={a.ansvar}
            node={a}
            expanded={openSet.has(`a-${a.ansvar}`)}
            onToggle={() => toggleNode(`a-${a.ansvar}`)}
          />
        ))}
      </Collapse>
    </>
  );
}

function SektorRow({
  node,
  expanded,
  onToggle,
  openSet,
  toggleNode,
}: {
  node: SektorNode;
  expanded: boolean;
  onToggle: () => void;
  openSet: Set<string>;
  toggleNode: (id: string) => void;
}) {
  const theme = useTheme();
  const sektorColor = SEKTOR_COLORS[node.sektorID] || theme.palette.primary.main;

  return (
    <>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 1,
          pr: 2,
          py: 1,
          cursor: 'pointer',
          bgcolor: alpha(sektorColor, 0.06),
          borderLeft: `4px solid ${sektorColor}`,
          '&:hover': {
            bgcolor: alpha(sektorColor, 0.12),
            boxShadow: `0 2px 8px ${alpha(sektorColor, 0.15)}`,
          },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          transition: 'all 0.15s ease-in-out',
        }}
      >
        <ExpandIcon open={expanded} />
        <AccountTreeIcon sx={{ fontSize: 22, color: sektorColor, mx: 0.5 }} />
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700, color: sektorColor }}>
          {node.sektorNavn}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ width: 130, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}
        >
          {formatKr(node.budsjett)}
        </Typography>
        <Box sx={{ width: 140, display: 'flex', justifyContent: 'center' }}>
          <DekBar budsjett={node.budsjett} dekning={node.dekning} />
        </Box>
        <Box sx={{ width: 70, textAlign: 'center' }}>
          <Chip
            label={`${node.antallAvtaler}`}
            size="small"
            sx={{ height: 22, fontWeight: 700, bgcolor: alpha(sektorColor, 0.15), color: sektorColor }}
          />
        </Box>
      </Box>
      <Collapse in={expanded} timeout={250}>
        {node.rammeomraader.map((r) => (
          <RammeRow
            key={r.rammeomraadeID}
            node={r}
            expanded={openSet.has(`r-${r.rammeomraadeID}`)}
            onToggle={() => toggleNode(`r-${r.rammeomraadeID}`)}
            openSet={openSet}
            toggleNode={toggleNode}
          />
        ))}
      </Collapse>
    </>
  );
}

/* ─── Main component ─── */

interface BudgetTreeViewProps {
  rows: TreeRow[];
  loading?: boolean;
}

export function BudgetTreeView({ rows, loading }: BudgetTreeViewProps) {
  const theme = useTheme();
  const [openSet, setOpenSet] = useState<Set<string>>(() => new Set());

  const { tree, totalBudsjett, totalDekning } = useMemo(() => buildTree(rows), [rows]);

  // Collect all expandable node IDs
  const allNodeIds = useMemo(() => {
    const ids: string[] = [];
    for (const s of tree) {
      ids.push(`s-${s.sektorID}`);
      for (const r of s.rammeomraader) {
        ids.push(`r-${r.rammeomraadeID}`);
        for (const a of r.ansvarListe) {
          ids.push(`a-${a.ansvar}`);
        }
      }
    }
    return ids;
  }, [tree]);

  const toggleNode = useCallback((id: string) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setOpenSet(new Set(allNodeIds)), [allNodeIds]);
  const collapseAll = useCallback(() => setOpenSet(new Set()), []);

  const totalDek = dekningsgrad(totalDekning, totalBudsjett);

  if (loading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  if (tree.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Ingen inntektslinjer funnet</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Totals + controls */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: `2px solid ${theme.palette.primary.main}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Totalt
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem' }}>
          {formatKr(totalBudsjett)}
        </Typography>
        {totalDek != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Dekningsgrad:</Typography>
            <DekBar budsjett={totalBudsjett} dekning={totalDekning} />
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <ButtonGroup size="small" variant="outlined">
          <Button startIcon={<UnfoldMoreIcon />} onClick={expandAll}>
            Vis alle
          </Button>
          <Button startIcon={<UnfoldLessIcon />} onClick={collapseAll}>
            Skjul alle
          </Button>
        </ButtonGroup>
      </Paper>

      {/* Column header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 0.5,
          bgcolor: 'grey.50',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 4, fontWeight: 600 }}>
          Navn
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ width: 130, textAlign: 'right', fontWeight: 600 }}>
          Budsjett
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ width: 140, textAlign: 'center', fontWeight: 600 }}>
          Dekningsgrad
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ width: 70, textAlign: 'center', fontWeight: 600 }}>
          Avtaler
        </Typography>
      </Box>

      {/* Tree */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {tree.map((s) => (
          <SektorRow
            key={s.sektorID}
            node={s}
            expanded={openSet.has(`s-${s.sektorID}`)}
            onToggle={() => toggleNode(`s-${s.sektorID}`)}
            openSet={openSet}
            toggleNode={toggleNode}
          />
        ))}
      </Paper>
    </Box>
  );
}
